import { useEffect, useState, useMemo, useCallback } from 'react';
import { CARD_PADDING } from 'design-system/src/constants';
import { PageContainer, CenteredContent, CardContent, PAGE_PADDING } from './styles';
import { ProgressCard } from 'design-system/src/components/ProgressCard/ProgressCard';
import Button from 'design-system/src/components/Button/Button';
import { ErrorMessage } from 'design-system/src/components/ErrorMessage/ErrorMessage';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { SignupType } from './utils';
import { client } from '../../api';
import { useMutation } from '@tanstack/react-query';
import { SignupFormState } from './types';
import { submitHubspotForm } from '../../utils/conversionTracking';
import { buildChiliPiperLeadData, useChiliPiper, CHILI_PIPER_LOAD_ERROR_MESSAGE } from '../../utils/chiliPiper';

const CHILI_PIPER_CONTAINER_ID = 'chili-piper-calendar';
const CHILI_PIPER_EMBED_HEIGHT = 650;

// Responsive width breakpoints (matching CalendarEmbed pattern)
const DESKTOP_EMBED_WIDTH = 880;
const SMALL_DESKTOP_EMBED_WIDTH = 600;
const TABLET_EMBED_WIDTH = 400;
const MOBILE_EMBED_WIDTH = 300;
const TOTAL_SURROUNDING_PADDING = CARD_PADDING * 2 + PAGE_PADDING * 2;

// Breakpoints for responsive width (min-width values)
const DESKTOP_BREAKPOINT = DESKTOP_EMBED_WIDTH + TOTAL_SURROUNDING_PADDING;
const SMALL_DESKTOP_BREAKPOINT = SMALL_DESKTOP_EMBED_WIDTH + TOTAL_SURROUNDING_PADDING;
const TABLET_BREAKPOINT = TABLET_EMBED_WIDTH + TOTAL_SURROUNDING_PADDING;

/**
 * Hook to get responsive width for Chili Piper container based on window size.
 * Chili Piper uses absolute positioning internally, so we need explicit dimensions.
 */
const useResponsiveEmbedWidth = () => {
  const [width, setWidth] = useState(MOBILE_EMBED_WIDTH);

  useEffect(() => {
    const updateWidth = () => {
      const windowWidth = window.innerWidth;
      if (windowWidth >= DESKTOP_BREAKPOINT) {
        setWidth(DESKTOP_EMBED_WIDTH);
      } else if (windowWidth >= SMALL_DESKTOP_BREAKPOINT) {
        setWidth(SMALL_DESKTOP_EMBED_WIDTH);
      } else if (windowWidth >= TABLET_BREAKPOINT) {
        setWidth(TABLET_EMBED_WIDTH);
      } else {
        setWidth(MOBILE_EMBED_WIDTH);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  return width;
};

type DemoContentProps = {
  showSkipButton: boolean;
  onSkip: () => void;
  isLoading: boolean;
  scriptError: boolean;
  embedWidth: number;
};

const DemoContent = ({ showSkipButton, onSkip, isLoading, scriptError, embedWidth }: DemoContentProps) => (
  <CardContent>
    <CenteredContent data-chromatic="ignore">
      {scriptError ? (
        <ErrorMessage textStyledAs="bodySmallDMSans">{CHILI_PIPER_LOAD_ERROR_MESSAGE}</ErrorMessage>
      ) : (
        <>
          {/* Styles for Chili Piper's injected iframe */}
          <style>{`#${CHILI_PIPER_CONTAINER_ID} iframe { border: none; width: 100% !important; height: 100% !important; }`}</style>
          <div
            id={CHILI_PIPER_CONTAINER_ID}
            className="relative mx-auto"
            style={{ width: embedWidth, height: CHILI_PIPER_EMBED_HEIGHT }}
          >
            {isLoading && <LoadingSpinner />}
          </div>
        </>
      )}
    </CenteredContent>
    {showSkipButton && (
      <Button variant="secondary" onClick={onSkip} width="full" className="mt-4">
        Skip to setting up my account
      </Button>
    )}
  </CardContent>
);

export type ScheduleDemoProps = {
  className?: string;
  onNext: () => void;
  onSkip: () => void;
  signupType: SignupType;
  signupId: string;
  formState?: SignupFormState;
  canSetUpAccount: boolean;
};

export const ScheduleDemo = ({
  className,
  onNext,
  onSkip,
  signupId,
  formState,
  canSetUpAccount,
}: ScheduleDemoProps) => {
  const embedWidth = useResponsiveEmbedWidth();

  const recordDemoBooking = useMutation({
    mutationFn: async () => {
      await client.post('/signup/record-demo-booking', {
        signup_id: signupId,
      });
    },
    onSuccess: async () => {
      const email = formState?.email ?? localStorage.getItem('email') ?? undefined;

      try {
        await submitHubspotForm('demo', {
          ...(formState ?? {}),
          email,
          booked_demo: true,
        });
      } catch (err) {
        // Silently fail - this is just for tracking, shouldn't block the flow
        console.error('Failed to submit to HubSpot', err);
      }
      onNext();
    },
  });

  // Build lead data from form state, with localStorage email fallback
  const leadData = useMemo(() => {
    const data = buildChiliPiperLeadData(formState);
    if (!data.email) {
      const storedEmail = localStorage.getItem('email');
      if (storedEmail) {
        data.email = storedEmail;
      }
    }
    return data;
  }, [formState]);

  const handleSuccess = useCallback(() => {
    recordDemoBooking.mutate();
  }, [recordDemoBooking]);

  const { isLoading, scriptError } = useChiliPiper({
    containerId: CHILI_PIPER_CONTAINER_ID,
    router: 'inbound-router',
    trigger: 'ThirdPartyForm',
    leadData,
    onSuccess: handleSuccess,
  });

  return (
    <PageContainer className={className}>
      <ProgressCard progressBar={{ current: 3, total: 3 }} desktopWidth="auto">
        <DemoContent
          showSkipButton={canSetUpAccount}
          onSkip={onSkip}
          isLoading={isLoading}
          scriptError={scriptError}
          embedWidth={embedWidth}
        />
      </ProgressCard>
    </PageContainer>
  );
};

export default ScheduleDemo;
